<!-- UiTabs.vue -->
<template>
  <div class="ui-tabs" :class="{ 'ui-tabs--scrollable': scrollable, 'ui-tabs--scroll-btn-inset': scrollBtnInset }">
    <div class="ui-tabs__nav-wrapper">
      <button v-if="scrollable && showLeftButton" class="ui-tabs__scroll-btn ui-tabs__scroll-btn--left" aria-label="이전 탭" @click="scrollLeft">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M13.9955 16.4463C13.7492 16.72 13.3277 16.7422 13.054 16.4959L6.3873 10.4961C6.2468 10.3697 6.1666 10.1896 6.1666 10.0006C6.1666 9.8116 6.2468 9.63149 6.3873 9.50506L13.054 3.50465C13.3276 3.25834 13.7492 3.28051 13.9955 3.55418C14.2418 3.82784 14.2196 4.24937 13.946 4.49568L7.8298 10.0005L13.9459 15.5048C14.2196 15.7511 14.2418 16.1726 13.9955 16.4463Z"
            fill="#464C53"
          />
        </svg>
      </button>

      <!-- 탭 네비게이션 -->
      <div ref="navElement" class="ui-tabs__nav" role="tablist" @scroll="handleScroll" :class="{ sortableList: draggable }">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.id"
          :id="`tab-${tab.id}`"
          :aria-controls="`panel-${tab.id}`"
          :aria-selected="activeTab === tab.id"
          class="row"
          :class="['ui-tabs__nav-item', { 'ui-tabs__nav-item--active': activeTab === tab.id }, { 'flex-1': width === 'full' }]"
          :style="getTabItemStyle()"
          :data-item-id="tab.id"
          role="tab"
          @click="handleTabClick(tab.id)"
        >
          <span v-if="draggable" class="sortIcon" title="순서변경" style="cursor: move; margin-left: auto">⋮⋮</span>

          <!-- 기본 모드: 텍스트 표시 -->
          <span v-if="editingTabId !== tab.id" class="tab-label">{{ tab.label }}</span>

          <!-- 편집 모드: 입력 필드 -->
          <div v-else class="tab-edit-container" @click.stop>
            <input ref="editInput" v-model="editingLabel" class="tab-edit-input" @keydown.enter="saveEdit" @keydown.escape="cancelEdit" @click.stop />
            <div class="tab-edit-buttons">
              <UiButton size="xxsmall" variant="ghost" iconOnly @click.stop="saveEdit" class="save-btn">
                <i class="icon icon-check icon-sm"></i>
              </UiButton>
              <UiButton size="xxsmall" variant="ghost" iconOnly @click.stop="cancelEdit" class="cancel-btn">
                <i class="icon icon-close icon-sm"></i>
              </UiButton>
            </div>
          </div>

          <UiButton v-if="draggable && editingTabId !== tab.id" size="xxsmall" variant="ghost" iconOnly @click.stop="handleEditTab(tab.id)">
            <i class="icon icon-pencil icon-sm"></i>
          </UiButton>
          <UiButton v-if="draggable" size="xxsmall" variant="ghost" iconOnly @click.stop="handleDeleteTab(tab.id)">
            <i class="icon icon-delete icon-sm"></i>
          </UiButton>
          <span v-if="isBadge && badge[index]" class="badge">{{ badge[index] }}</span>
        </button>
      </div>

      <!-- 우측 스크롤 버튼 -->
      <button v-if="scrollable && showRightButton" class="ui-tabs__scroll-btn ui-tabs__scroll-btn--right" aria-label="다음 탭" @click="scrollRight">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.00451 16.4463C6.25081 16.72 6.67234 16.7422 6.94601 16.4959L13.6127 10.4961C13.7532 10.3697 13.8334 10.1896 13.8334 10.0006C13.8334 9.8116 13.7532 9.63149 13.6127 9.50506L6.94604 3.50465C6.67237 3.25834 6.25085 3.28051 6.00453 3.55418C5.75822 3.82784 5.78039 4.24937 6.05405 4.49568L12.1702 10.0005L6.05408 15.5048C5.7804 15.7511 5.75821 16.1726 6.00451 16.4463Z"
            fill="#464C53"
          />
        </svg>
      </button>
    </div>

    <div class="ui-tabs__content" :class="{ 'is-scroll': isScroll }">
      <!-- 각 탭에 해당하는 슬롯을 표시 -->
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="['ui-tabs__panel', { 'ui-tabs__panel--active': activeTab === tab.id }]"
        :id="`panel-${tab.id}`"
        role="tabpanel"
        :aria-labelledby="`tab-${tab.id}`"
      >
        <slot :name="tab.slot ?? tab.label" :tab="tab" :activeTab="activeTab"></slot>
      </div>
    </div>

    <!-- 삭제 확인 모달 -->
    <TabDeleteConfirmModal
      :is-visible="showDeleteModal"
      :tab-id="deleteTargetTab?.id"
      :tab-label="deleteTargetTab?.label"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import TabDeleteConfirmModal from '@/components/domain/hr-evaluation/modals/TabDeleteConfirmModal.vue';

const props = defineProps({
  tabs: {
    type: Array,
    required: true,
    validator: tabs => tabs.every(tab => tab.id && tab.label),
  },
  defaultTab: {
    type: String,
    default: '',
  },
  modelValue: {
    type: String,
    default: '',
  },
  isScroll: {
    type: Boolean,
    default: false,
  },
  badge: {
    type: Array,
    default: () => [],
  },
  isBadge: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: [String, Number],
    default: null,
  },
  minWidth: {
    type: [String, Number],
    default: null,
  },
  scrollable: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: '',
  },
  draggable: {
    type: Boolean,
    default: false,
  },
  scrollBtnInset: {
    type: Boolean,
    default: false,
  },
});

// 스크롤 관련 반응형 데이터 추가
const navElement = ref(null);
const showLeftButton = ref(false);
const showRightButton = ref(false);
/**
 * 스크롤 상태 확인 함수
 */
const checkScrollButtons = () => {
  if (!navElement.value || !props.scrollable) return;

  const { scrollLeft, scrollWidth, clientWidth } = navElement.value;

  showLeftButton.value = scrollLeft > 0;
  showRightButton.value = scrollLeft < scrollWidth - clientWidth - 1;
};
/**
 * 우측 스크롤 함수 - 한 화면 분량만큼 이동
 */
const scrollRight = () => {
  if (!navElement.value) return;

  const container = navElement.value;
  const tabs = container.querySelectorAll('.ui-tabs__nav-item');

  // 현재 보이는 영역
  const containerLeft = container.scrollLeft;
  const containerRight = containerLeft + container.clientWidth;

  // 현재 화면에서 완전히 보이는 마지막 탭 찾기
  let lastVisibleIndex = -1;

  for (let i = 0; i < tabs.length; i++) {
    const tab = tabs[i];
    const tabLeft = tab.offsetLeft;
    const tabRight = tabLeft + tab.offsetWidth;

    // 탭이 완전히 보이는 경우
    if (tabLeft >= containerLeft && tabRight <= containerRight) {
      lastVisibleIndex = i;
    }
  }

  // 다음 탭으로 이동 (완전히 보이지 않는 첫 번째 탭)
  const nextTabIndex = lastVisibleIndex + 1;

  if (nextTabIndex < tabs.length) {
    const targetTab = tabs[nextTabIndex];

    // 해당 탭이 완전히 보이도록 스크롤
    const targetScrollLeft = targetTab.offsetLeft;

    container.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  }
};

/**
 * 좌측 스크롤 함수 - 한 화면 분량만큼 뒤로 이동
 */
const scrollLeft = () => {
  if (!navElement.value) return;

  const container = navElement.value;
  const tabs = container.querySelectorAll('.ui-tabs__nav-item');

  // 현재 보이는 영역
  const containerLeft = container.scrollLeft;
  const containerRight = containerLeft + container.clientWidth;

  // 현재 화면에서 완전히 보이는 첫 번째 탭 찾기
  let firstVisibleIndex = -1;

  for (let i = 0; i < tabs.length; i++) {
    const tab = tabs[i];
    const tabLeft = tab.offsetLeft;
    const tabRight = tabLeft + tab.offsetWidth;

    // 탭이 완전히 보이는 경우
    if (tabLeft >= containerLeft && tabRight <= containerRight) {
      firstVisibleIndex = i;
      break;
    }
  }

  if (firstVisibleIndex > 0) {
    // 한 화면 분량만큼 뒤로 계산
    const containerWidth = container.clientWidth;
    let targetIndex = firstVisibleIndex - 1;
    let totalWidth = 0;

    // 한 화면에 들어갈 수 있는 탭들을 역순으로 계산
    for (let i = targetIndex; i >= 0; i--) {
      const tab = tabs[i];
      totalWidth += tab.offsetWidth;

      if (totalWidth > containerWidth) {
        targetIndex = i + 1;
        break;
      }

      if (i === 0) {
        targetIndex = 0;
      }
    }

    const targetTab = tabs[targetIndex];
    container.scrollTo({
      left: targetTab.offsetLeft,
      behavior: 'smooth',
    });
  }
};
/**
 * 스크롤 이벤트 핸들러
 */
const handleScroll = () => {
  checkScrollButtons();
};

/**
 * 드래그 앤 드롭 핸들러
 */
const { $Sortable } = useNuxtApp();
let sortableInstance = null;
const initSortable = async () => {
  sortableInstance?.destroy?.();
  sortableInstance = null;

  await nextTick();
  const container = document.querySelector('.sortableList');
  if (!container) return;

  sortableInstance = new $Sortable(container, {
    handle: '.sortIcon',
    animation: 150,
    draggable: '.row',
    onEnd() {
      const ids = Array.from(container.querySelectorAll('.row[data-item-id]')).map(el => el.getAttribute('data-item-id'));
      emit('row-drag-end', [...new Set(ids)].join(','));
    },
  });
};
watch(
  () => props.draggable,
  open => {
    if (open) initSortable();
    else {
      sortableInstance?.destroy?.();
      sortableInstance = null;
    }
  },
);
onMounted(() => {
  if (props.draggable) initSortable();
});

/**
 * 리사이즈 이벤트 핸들러
 */
const handleResize = () => {
  checkScrollButtons();
};

const emit = defineEmits(['update:modelValue', 'change', 'tab-edit', 'tab-delete', 'row-drag-end']);

const activeTab = ref(props.modelValue || props.defaultTab || props.tabs[0]?.id || '');

// 편집 모드 관련 상태
const editingTabId = ref(null);
const editingLabel = ref('');
const originalLabel = ref('');
const editInput = ref(null);

// 삭제 확인 모달 관련 상태
const showDeleteModal = ref(false);
const deleteTargetTab = ref(null);

const handleTabClick = tabId => {
  // 편집 모드 중이고 다른 탭을 클릭한 경우 편집 취소
  if (editingTabId.value && editingTabId.value !== tabId) {
    cancelEdit();
  }

  setActiveTab(tabId);
};

const setActiveTab = tabId => {
  // 편집 모드 중에는 탭 전환 방지
  if (editingTabId.value) {
    return;
  }

  if (activeTab.value !== tabId) {
    console.log('setActiveTab', tabId);
    activeTab.value = tabId;
    emit('update:modelValue', tabId);
    emit('change', tabId);
  }
};

// 편집 모드 핸들러 함수들
const handleEditTab = tabId => {
  // 이미 편집 중인 탭이 있다면 취소
  if (editingTabId.value && editingTabId.value !== tabId) {
    cancelEdit();
  }

  const tab = props.tabs.find(t => t.id === tabId);
  if (tab) {
    editingTabId.value = tabId;
    editingLabel.value = tab.label;
    originalLabel.value = tab.label;

    // 다음 틱에서 입력 필드에 포커스
    nextTick(() => {
      if (editInput.value) {
        editInput.value.focus();
        editInput.value.select();
      }
    });
  }
};

const saveEdit = () => {
  if (editingTabId.value && editingLabel.value.trim()) {
    const tabId = editingTabId.value;
    const newLabel = editingLabel.value.trim();

    // 부모 컴포넌트에 편집 완료 이벤트 전달
    emit('tab-edit', { tabId, newLabel });

    // 편집 모드 종료
    editingTabId.value = null;
    editingLabel.value = '';
    originalLabel.value = '';
  }
};

const cancelEdit = () => {
  editingTabId.value = null;
  editingLabel.value = '';
  originalLabel.value = '';
};

const handleDeleteTab = tabId => {
  const tab = props.tabs.find(t => t.id === tabId);
  if (tab) {
    deleteTargetTab.value = tab;
    showDeleteModal.value = true;
  }
};

// 모달 핸들러
const handleDeleteConfirm = tabId => {
  emit('tab-delete', tabId);
  showDeleteModal.value = false;
  deleteTargetTab.value = null;
};

const handleDeleteCancel = () => {
  showDeleteModal.value = false;
  deleteTargetTab.value = null;
};

// v-model 지원
watch(
  () => props.modelValue,
  newValue => {
    if (newValue && newValue !== activeTab.value) {
      activeTab.value = newValue;
    }
  },
);

// 키보드 네비게이션
const handleKeydown = event => {
  const currentIndex = props.tabs.findIndex(tab => tab.id === activeTab.value);
  let newIndex = currentIndex;

  switch (event.key) {
    case 'ArrowLeft':
      newIndex = currentIndex > 0 ? currentIndex - 1 : props.tabs.length - 1;
      break;
    case 'ArrowRight':
      newIndex = currentIndex < props.tabs.length - 1 ? currentIndex + 1 : 0;
      break;
    case 'Home':
      newIndex = 0;
      break;
    case 'End':
      newIndex = props.tabs.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  setActiveTab(props.tabs[newIndex].id);
};

// 👇 스타일 계산 함수 추가
const getTabItemStyle = () => {
  const style = {};

  if (props.maxWidth) {
    style.maxWidth = typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth;
  }

  if (props.minWidth) {
    style.minWidth = typeof props.minWidth === 'number' ? `${props.minWidth}px` : props.minWidth;
  }

  return style;
};

onMounted(() => {
  const navElement = document.querySelector('.ui-tabs__nav');
  navElement?.addEventListener('keydown', handleKeydown);
  // 스크롤 관련 이벤트 리스너 추가
  if (props.scrollable) {
    nextTick(() => {
      checkScrollButtons();
      window.addEventListener('resize', handleResize);
    });
  }
});

onUnmounted(() => {
  const navElement = document.querySelector('.ui-tabs__nav');
  navElement?.removeEventListener('keydown', handleKeydown);
  // 스크롤 관련 이벤트 리스너 제거
  window.removeEventListener('resize', handleResize);
});
// tabs 변경 감지하여 스크롤 버튼 상태 업데이트
watch(
  () => props.tabs,
  () => {
    if (props.scrollable) {
      nextTick(() => {
        checkScrollButtons();
      });
    }
  },
  { deep: true },
);
</script>

<style scoped lang="scss">
// 기본 색상 변수
$primary-color: #00aaff;

$gray-5: #f9f9f9;
$gray-10: #e5e5e5;
$gray-30: #999;
$gray-50: #666;
$gray-70: #333;

.ui-tabs {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.ui-tabs__nav {
  display: flex;
  border-bottom: 1px solid $gray-10;
  background: #fff;
}

.ui-tabs__nav-item {
  // flex: 1;
  padding: 10px 16px;
  @include font-style($body-medium);
  color: $gray-30;
  position: relative;

  // 🔥 border-radius 명시적 제거
  border-radius: 0 !important;
  border: none !important;

  // 모든 상태에서 동일한 높이 유지
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  // 👇 동적 width 스타일 추가
  &[data-max-width] {
    max-width: var(--max-width);
  }

  &[data-min-width] {
    min-width: var(--min-width);
  }

  .badge {
    position: absolute;
    top: 11px;
    right: 74px;
    background-color: #f30;
    color: white;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    line-height: 1;
    font-weight: 600;
    transition: none !important;
    pointer-events: none;
    z-index: 1;
  }

  &:hover {
    background-color: rgba(0, 170, 255, 0.06);
    color: $gray-70;
    border-radius: 0 !important;
  }

  &:focus {
    outline: 2px solid $primary-color;
    outline-offset: -2px;
    border-radius: 0 !important;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: transparent;
    z-index: 1;
  }

  &--active {
    font-weight: 700;
    color: $primary-color;

    border-radius: 0 !important;
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;

    &::after {
      background-color: $primary-color;
    }
  }
}

.ui-tabs__content {
  background: #fff;

  &.is-scroll {
    max-height: 400px;
    overflow-y: auto;
  }
}

.ui-tabs__panel {
  display: none;

  &--active {
    display: block;
  }
}

// 반응형
@media (max-width: 768px) {
  .ui-tabs__nav {
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .ui-tabs__nav-item {
    white-space: nowrap;
    flex-shrink: 0;
    min-width: 100px;
    padding: 10px 16px;
    font-size: 13px;

    // 🔥 모바일에서도 border-radius 제거
    border-radius: 0 !important;
  }

  .ui-tabs__panel {
    padding: 16px;
  }
}

// 🔥 추가: 전역 스타일 재정의 방지
* {
  &.ui-tabs__nav-item {
    border-radius: 0 !important;

    &:before,
    &:after {
      border-radius: 0 !important;
    }
  }
}

.ui-tabs {
  &--scrollable {
    .ui-tabs__nav-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .ui-tabs__nav {
      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
      scroll-behavior: smooth;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
}

.ui-tabs__nav-wrapper {
  position: relative;
  background: #fff;
  overflow: hidden;
}

.ui-tabs__scroll-btn {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40px;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }

  &--left {
    left: 0;
    z-index: 10;
    border: none;
    background: #fff;
    width: 40px;
  }

  &--right {
    right: 0;
    z-index: 10;
    border: none;
    background: #fff;
    width: 40px;
  }

  svg {
    color: #464c53;
  }
}

/* 스크롤 버튼을 탭 영역 안쪽(left:0, right:0)으로 두고 호버 효과 적용 - scrollBtnInset 사용 시 */
.ui-tabs--scroll-btn-inset {
  .ui-tabs__scroll-btn {
    transition:
      background-color 0.2s ease,
      box-shadow 0.2s ease,
      opacity 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &:active {
      background: rgba(245, 245, 245, 1);
    }
  }

  .ui-tabs__scroll-btn--left {
    left: 0;
  }

  .ui-tabs__scroll-btn--right {
    right: 0;
  }
}

// scrollable일 때 nav 스타일 조정
// .ui-tabs--scrollable .ui-tabs__nav 스타일은 필요시 추가

.ui-button {
  &--xxsmall {
    padding: 2px 2px;
    font-size: $ui-font-xs;
    justify-content: center;
    align-items: center;
    gap: 2px;
  }
}

// 편집 모드 스타일
.tab-label {
  flex: 1;
  text-align: center;
}

.tab-edit-container {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.tab-edit-input {
  flex: 0.67; // 2/3 크기로 조정
  border: 1px solid $primary-color;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: inherit;
  font-family: inherit;
  background: #fff;
  color: $gray-70;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 2px rgba(0, 170, 255, 0.2);
  }
}

.tab-edit-buttons {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.save-btn {
  color: #28a745 !important;

  &:hover {
    background-color: rgba(40, 167, 69, 0.1) !important;
  }
}

.cancel-btn {
  color: #dc3545 !important;

  &:hover {
    background-color: rgba(220, 53, 69, 0.1) !important;
  }
}
</style>
